<template>
    <div class="project-show__wrapper">
        <div class="app__card_show project-show">
            <div class="app__card-header project-show__card-header">
                <div>{{ $t("projects.projectTitle", {name: project.viewName}) }}</div>
                <div>
                    <span
                        class="project-show__anchor-icon project-show__icon_space_before"
                        v-if="$features.configureProjects"
                        @click="displayConfiguration"
                        v-html="$tc('icons.configure')"
                        :title="$tc('portalActions.configure')"
                    ></span>
                    <span
                        class="project-show__anchor-icon project-show__icon_space_before"
                        v-html="$tc('icons.preview')"
                        @click="playPortal()"
                        :title="$tc('portalActions.play')"
                    ></span>
                    <span
                        v-if="$features.editProjects"
                        class="project-show__anchor-icon project-show__icon_space_before"
                        @click="$emit('edit', project)"
                        v-html="$tc('icons.edit')"
                        :title="$tc('portalActions.edit')"
                    ></span>
                    <span
                        class="project-show__anchor-icon project-show__icon_space_before"
                        v-if="$features.deleteProjects && !isDefaultProject"
                        @click="deleteProject"
                        v-html="$tc('icons.delete')"
                        :title="$tc('portalActions.delete')"
                    ></span>
                </div>
            </div>
            <div class="project-show__card-body">
                <div class="project-show__content  project-show__content_centered">
                    <sah-button @click.native="newCategory" class="project-show__create-cat-btn">{{
                        $tc("projects.createCategory")
                    }}</sah-button>
                    <draggable
                        class="app__project-categories-list"
                        v-model="projectCategories"
                        :group="projectCategoriesGroup"
                        @change="changeProjectCategories"
                    >
                        <category-show
                            v-for="category in projectCategories"
                            :key="category.id"
                            :category="category"
                            :project="project"
                        ></category-show>
                    </draggable>
                </div>
            </div>
        </div>
        <category-edit
            v-if="isEditing"
            :category="editedCategory"
            :project="project"
            :operation="editOperation"
            @cancel="closeEdit"
            @end="closeEdit"
        ></category-edit>
        <project-configuration
            v-if="isDisplayingConfiguration"
            :project="project"
            @close="closeConfigurationDisplay"
        ></project-configuration>
    </div>
</template>
<script>
import {store, SahButton} from "atom";
import CategoryEdit from "./CategoryEdit";
import CategoryShow from "./CategoryShow";
import ProjectConfiguration from "./ProjectConfiguration";
import draggable from "vuedraggable";
import {EDIT_OPERATION_STORE, EDIT_OPERATION_UPDATE} from "../constants";
const {createNamespacedHelpers} = store;
const {mapActions, mapGetters, mapState} = createNamespacedHelpers("arcman");

export default {
    name: "project-show",
    props: {
        project: {type: Object, required: true},
    },
    components: {
        CategoryEdit,
        CategoryShow,
        draggable,
        ProjectConfiguration,
        SahButton,
    },
    computed: {
        ...mapGetters(["currentLocale"]),
        ...mapState(["applications", "arcologyConstants"]),
        projectCategoriesGroup() {
            return {name: "projectCategoriesGroup", pull: true, put: ["projectCategoriesGroup"]};
        },
        isDefaultProject() {
            return this.project.name === this.$settings.dataModels.DEFAULT_PROJECT_NAME;
        },
    },
    watch: {
        project(newValue, oldValue) {
            this.projectCategories = this.project.categories;
        },
    },
    methods: {
        ...mapActions({
            destroyProject: "DELETE_PROJECT",
            swapProjectCategories: "SWAP_PROJECT_CATEGORIES",
        }),
        changeProjectCategories(event) {
            this.swapProjectCategories({
                projectId: this.project.id,
                categories: this.projectCategories,
            });
        },
        deleteProject() {
            this.$modal.dialog(this.$t("projects.confirmDelete", {name: this.project.viewName})).then(confirm => {
                if (confirm) {
                    this.destroyProject(this.project);
                }
                this.$modal.hide();
            });
        },
        getCategoryTemplate() {
            return JSON.parse(JSON.stringify(this.arcologyConstants.CATEGORY_TEMPLATE));
        },
        editCategory(event) {
            this.isEditing = true;
            this.editOperation = EDIT_OPERATION_UPDATE;
            this.editedCategory = Object.assign(this.getCategoryTemplate(), event);
        },
        newCategory() {
            this.isEditing = true;
            this.editOperation = EDIT_OPERATION_STORE;
            this.editedCategory = this.getCategoryTemplate();
        },
        closeEdit() {
            this.isEditing = false;
            this.editedCategory = {};
        },
        displayConfiguration() {
            this.isDisplayingConfiguration = true;
        },
        closeConfigurationDisplay() {
            this.isDisplayingConfiguration = false;
        },
        playPortal() {
            let url = new URL(this.$settings.appLauncher.url);
            url.searchParams.append("app-appStore.portal", this.project.name.replace("generic_", "").toLowerCase());
            url.searchParams.append("languages.default", this.currentLocale);
            url.searchParams.append("clients.0.options.apiKey", this.$settings.apiKey);
            url.searchParams.append("app-appStore.disableNativeApps", true);
            url.searchParams.append("global.isTVScreen", true);
            url.searchParams.append("logger.disabled", true);
            url.searchParams.append("externalApplications.defaultType", "iframe");
            url.searchParams.append("noAppManager", true);
            window.open(url.href, "_blank");
        },
    },
    data: () => {
        return {
            isDisplayingConfiguration: false,
            isEditing: false,
            projectCategories: [],
            editOperation: "",
            editedCategory: {},
        };
    },
    mounted() {
        this.projectCategories = this.project.categories;
    },
};
</script>

<style lang="scss" scoped>
.project-show__wrapper::v-deep {
    .project-show {
        border-color: $border-project-card-color;
    }

    .project-show__card-header {
        background-color: $background-project-card-header-color;
    }

    .project-show__card-body {
        @apply overflow-auto;
        height: calc(100vh - 13rem);
        @screen lg {
            height: calc(100vh - 14rem);
        }
    }

    .slide-group__content {
        @apply flex flex-row;
    }

    .project-show__tab-item {
        @apply p-2 mr-2 rounded-full text-white;
        background-color: $background-tab-item-color;
        &:hover {
            @apply cursor-pointer;
        }
    }

    .tab-item_active {
        background-color: $background-tab-item-active-color;
    }

    .project-show__content {
        @apply flex flex-col items-center;
    }

    .project-show__content_centered {
        @apply items-center;
    }

    .project-show__icon_space_before {
        @apply ml-4;
    }

    .project-show__icon_space_around {
        @apply mx-4;
    }

    .project-show__anchor-icon {
        &:hover {
            @apply cursor-pointer;
        }
    }

    .application-display-field {
        @apply flex w-full mb-1;
    }

    .application-display-field__label {
        @apply font-bold mr-2;
    }

    .application-display-field__value {
        @apply break-all;
    }

    .project-show__create-cat-btn {
        @apply my-4;
    }
}
</style>
